import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, takeUntil, tap, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { organisationSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { GeoidService } from '../service/geoid.abstract.service';

export interface IGeoidAddressDialogData {
    title?: string;
    streetName?: string;
    city?: string;
    postalCode?: string;
    unit?: string;
    showSubTypeSelector?: boolean;
}

@Component({
    selector: 'app-geoid-address-modal',
    templateUrl: './geoid-address-modal.component.html',
    styleUrls: ['./geoid-address-modal.component.scss'],
})
export class GeoidAddressModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    public view: 'search' | 'list' = 'search';

    public geoIdSearchForm: FormGroup;

    public geoIdAddressForm: FormGroup;

    public listData = [];

    public listDataForceSearch = [];

    public loading = false;

    public forceSearchUsed = false;

    private currentOrg;

    // SubType selector (opt-in via dialog data)
    public showSubTypeSelector = false;
    public selectedSubType = '';
    public subTypeOptions: { value: string; label: string }[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private geoidService: GeoidService,
        public dialogRef: MatDialogRef<GeoidAddressModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IGeoidAddressDialogData,
        private store: Store<State>,
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
    ) {
        if (data?.showSubTypeSelector) {
            this.showSubTypeSelector = true;
            this.subTypeOptions = [
                { value: 'RESIDENT', label: this.translateService.instant('pages.residences.subType.resident') },
                { value: 'NONRESIDENT', label: this.translateService.instant('pages.residences.subType.nonResident') },
            ];
        }
    }

    get streetAddress(): AbstractControl {
        return this.geoIdSearchForm.get('streetAddress');
    }

    get city(): AbstractControl {
        return this.geoIdSearchForm.get('city');
    }

    get postalCode(): AbstractControl {
        return this.geoIdSearchForm.get('postalCode');
    }

    get unit(): AbstractControl {
        return this.geoIdSearchForm.get('unit');
    }

    get address(): AbstractControl {
        return this.geoIdAddressForm.get('address');
    }

    ngOnInit(): void {
        this.geoIdSearchForm = this.formBuilder.group(
            {
                streetAddress: new FormControl('', [Validators.required]),
                city: new FormControl('', [Validators.required, Validators.pattern(/^(?=.*[a-zA-ZÀ-ÿŒœ])[a-zA-ZÀ-ÿŒœ\s'’.\-]+$/)]),
                postalCode: new FormControl('', [Validators.required]),
                unit: new FormControl('', []),
            },
            {
                updateOn: 'blur',
            },
        );
        this.geoIdAddressForm = this.formBuilder.group(
            {
                address: new FormControl('', [Validators.required]),
            },
            {
                updateOn: 'blur',
            },
        );

        this.store
            .select(organisationSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((org) => {
                    this.currentOrg = org;
                    this.city.setValue(org?.name || '');
                }),
            )
            .subscribe();

        // Handle dialog data
        if (this.data) {
            const { streetName, city, postalCode, unit } = this.data;

            if (streetName) this.streetAddress.setValue(streetName);
            if (city) this.city.setValue(city);
            if (postalCode) this.postalCode.setValue(postalCode);
            if (unit) this.unit.setValue(unit);
        }
    }

    search(): void {
        this.geoIdSearchForm.markAllAsTouched();

        if (this.geoIdSearchForm.valid) {
            this.loading = true;
            this.geoidService
                .searchAddress({
                    streetAddress: this.streetAddress.value,
                    city: this.city.value,
                    postalCode: this.postalCode.value,
                    unit: this.unit.value,
                })
                .pipe(
                    take(1),
                    tap((address) => {
                        this.listData = address.map((d) => {
                            const unit = d.unit ? d.unit : this.unit.value;
                            d.unit = unit;
                            return {
                                label: `<i class="icon-map"></i> ${d.unit} ${d.number} ${d.street}, ${d.city}, ${d.province}, ${d.postCode}`,
                                value: d,
                            };
                        });
                    }),
                    tap(() => {
                        this.view = 'list';
                        this.loading = false;
                        this.cdr.detectChanges();
                    }),
                    catchError(() => {
                        this.loading = false;
                        this.view = 'list';
                        this.listData = [];
                        return of([]);
                    }),
                )
                .subscribe();
        }
    }

    forceSearch(): void {
        this.geoIdSearchForm.markAllAsTouched();

        if (this.geoIdSearchForm.valid) {
            this.loading = true;
            this.geoidService
                .forceSearchAddress({
                    streetAddress: this.streetAddress.value,
                    city: this.city.value,
                    postalCode: this.postalCode.value,
                    unit: this.unit.value,
                })
                .pipe(
                    take(1),
                    tap((address) => {
                        if (address.length > 0) {
                            this.listDataForceSearch = address.map((d) => {
                                const unit = d.unit ? d.unit : this.unit.value;
                                d.unit = unit;
                                return {
                                    label: `<i class="icon-map"></i> ${d.unit} ${d.number} ${d.street}, ${d.city}, ${d.province}, ${d.postCode}`,
                                    value: d,
                                };
                            });
                        }
                    }),
                    tap(() => {
                        this.loading = false;
                        this.view = 'list';
                        this.forceSearchUsed = true;
                    }),
                    catchError(() => {
                        this.loading = false;
                        this.view = 'list';
                        this.listDataForceSearch = [];
                        this.forceSearchUsed = true;
                        return of([]);
                    }),
                )
                .subscribe();
        }
    }

    select(): void {
        if (this.showSubTypeSelector) {
            this.dialogRef.close({ address: this.address.value, subType: this.selectedSubType });
        } else {
            this.dialogRef.close(this.address.value);
        }
    }

    closeModal(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
